import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Check, XCircle, ArrowRight, Handshake } from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

/**
 * Public, no-auth confirmation page. When a brand clicks the tokenized link in
 * a pitch email footer, we land them here, POST to confirm, and show a friendly
 * thank-you. Idempotent — safe to reload.
 */
export default function ReplyConfirm() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | confirmed | already | expired | error
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get(`/r/${token}/status`);
        if (cancelled) return;
        setInfo(s.data);
        if (s.data.used) { setState("already"); return; }

        const c = await api.post(`/r/${token}/confirm`);
        if (cancelled) return;
        setInfo((prev) => ({ ...prev, ...c.data }));
        setState("confirmed");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setState(code === 410 ? "expired" : code === 404 ? "expired" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain flex flex-col">
      <SEO title="Reply confirmed · SourceDeck" description="Thanks for confirming your reply." path={`/r/${token}`} />

      <header className="border-b border-[#27272a]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
            <span className="font-display text-xl tracking-tight">SourceDeck</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg sd-card p-8 md:p-10 text-center" data-testid="reply-confirm-panel">
          {state === "loading" && (
            <>
              <Loader2 size={28} className="animate-spin text-[#FF3B30] mx-auto mb-4" />
              <div className="font-display text-2xl">Confirming your reply…</div>
            </>
          )}

          {state === "confirmed" && (
            <>
              <div className="w-14 h-14 flex items-center justify-center bg-[#34C759]/[0.15] border border-[#34C759]/60 mx-auto mb-5">
                <Check size={28} strokeWidth={1.5} className="text-[#34C759]" />
              </div>
              <div className="sd-label mb-2 text-[#34C759]">● REPLY CONFIRMED</div>
              <h1 className="font-display text-3xl md:text-4xl leading-tight mb-3" data-testid="reply-confirm-title">
                Thanks — {info.creator_name || "the creator"} will follow up
              </h1>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Your interest in the <b className="text-white">{info.brand}</b> partnership pitch has been logged.
                {info.creator_email && <> They'll reply to you at your email.</>}
              </p>
              <p className="text-[11px] font-mono text-zinc-500 mb-6">SourceDeck helps creators track brand replies. You're not signed up for anything — this is just a one-click confirmation.</p>
              <Link to="/" className="sd-btn sd-btn-primary h-11 px-5 inline-flex" data-testid="reply-confirm-explore-btn">
                What is SourceDeck? <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            </>
          )}

          {state === "already" && (
            <>
              <div className="w-14 h-14 flex items-center justify-center bg-[#FFC107]/[0.15] border border-[#FFC107]/60 mx-auto mb-5">
                <Handshake size={28} strokeWidth={1.5} className="text-[#FFC107]" />
              </div>
              <div className="sd-label mb-2 text-[#FFC107]">● ALREADY CONFIRMED</div>
              <h1 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                This reply was already logged
              </h1>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                You confirmed your reply to <b className="text-white">{info.brand}</b>'s pitch earlier. Nothing more to do — {info.creator_name || "the creator"} already knows.
              </p>
              <Link to="/" className="sd-btn sd-btn-ghost h-11 px-5 inline-flex">
                Explore SourceDeck
              </Link>
            </>
          )}

          {(state === "expired" || state === "error") && (
            <>
              <div className="w-14 h-14 flex items-center justify-center bg-[#FF3B30]/[0.12] border border-[#FF3B30]/60 mx-auto mb-5">
                <XCircle size={28} strokeWidth={1.5} className="text-[#FF3B30]" />
              </div>
              <div className="sd-label mb-2 text-[#FF3B30]">● LINK NOT ACTIVE</div>
              <h1 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                {state === "expired" ? "This link has expired" : "Something went wrong"}
              </h1>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                {state === "expired"
                  ? "Reply-confirm links are valid for 90 days after the original pitch. Please reply directly to the creator's email instead."
                  : "We couldn't process this link. Try again in a moment, or just reply to the creator's email."}
              </p>
              <Link to="/" className="sd-btn sd-btn-ghost h-11 px-5 inline-flex">
                Home
              </Link>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
